/**
 * Access process training (`/learn/access`, 13 Oct) — for everyone signed in.
 *
 * A sticky map of the whole process with the reader's steps lit, then one
 * chapter per role they hold: detected by the server (`/me/training-roles`),
 * adjustable here ("I also do…", kept in this browser). When a role cannot
 * be told (Ivanti slow), the picker opens instead of guessing. At the end,
 * "Confirm your role" against the roster. Content in English for now.
 *
 * Reached from the chat's access card and the access wizard (the soft
 * prompt and the "How this works" link), the footer, and IT Admin → Learning.
 */

import { Icon, Info } from "@norican/icons";
import { Alert, Checkbox, Typography } from "@norican/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingState,
  PageContainer,
  SectionCard,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { useTrainingRoles } from "./api";
import { ChapterView } from "./chapter";
import { ConfirmRoles } from "./confirm-roles";
import rawContent from "./lib/content.json";
import {
  chosenRoles,
  NO_CHOICE,
  type RoleChoice,
  roleForLane,
  toggleRole,
  TRAINING_ROLES,
  type TrainingContent,
} from "./lib/training";
import { ProcessMap } from "./process-map";

const CONTENT = rawContent as TrainingContent;
const STORAGE_PREFIX = "helpdesk.accessTraining.roles.";

function readChoice(email: string | undefined): RoleChoice {
  if (!email) {
    return NO_CHOICE;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + email.toLowerCase());
    const parsed = raw ? (JSON.parse(raw) as Partial<RoleChoice>) : null;
    return {
      added: Array.isArray(parsed?.added) ? parsed.added : [],
      removed: Array.isArray(parsed?.removed) ? parsed.removed : [],
    };
  } catch {
    return NO_CHOICE;
  }
}

function writeChoice(email: string | undefined, choice: RoleChoice) {
  if (!email) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_PREFIX + email.toLowerCase(), JSON.stringify(choice));
  } catch {
    // Private mode: the choice lasts for this visit only.
  }
}

export function AccessTrainingPage() {
  const { t } = useT();
  const { language } = useLanguage();
  useDocumentTitle(t("accessTraining.title"));
  const { data: me } = useMe();
  const roles = useTrainingRoles();
  const [choice, setChoice] = useState<RoleChoice | null>(null);
  const effectiveChoice = choice ?? readChoice(me?.email);

  const detected = useMemo(
    () => (roles.data?.roles ?? []).filter((r) => r.detected).map((r) => r.role),
    [roles.data],
  );
  const shown = chosenRoles(detected, effectiveChoice);
  const statusOf = (role: string) => roles.data?.roles.find((r) => r.role === role);

  const setRole = (role: string, on: boolean) => {
    const next = toggleRole(effectiveChoice, detected, role, on);
    setChoice(next);
    writeChoice(me?.email, next);
  };

  const goToLane = useCallback(
    (lane: string) => {
      const role = roleForLane(lane, shown, CONTENT);
      if (role) {
        document.getElementById(`role-${role}`)?.scrollIntoView({ behavior: "smooth" });
      }
    },
    [shown],
  );

  // "Show me" links to `#role-requester`: scroll there once the chapters exist.
  const { hash } = useLocation();
  const loaded = roles.data !== undefined;
  useEffect(() => {
    if (loaded && hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    }
  }, [loaded, hash]);

  const breadcrumbs = <AppBreadcrumbs items={[{ label: t("accessTraining.title") }]} />;

  if (roles.isPending) {
    return (
      <PageContainer title={t("accessTraining.title")} breadcrumbs={breadcrumbs} width="screen">
        <LoadingState />
      </PageContainer>
    );
  }
  if (roles.isError) {
    return (
      <PageContainer title={t("accessTraining.title")} breadcrumbs={breadcrumbs} width="screen">
        <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />
      </PageContainer>
    );
  }

  const data = roles.data;
  const undetermined = data.undetermined ?? [];
  return (
    <PageContainer
      title={t("accessTraining.title")}
      subtitle={t("accessTraining.subtitle")}
      breadcrumbs={breadcrumbs}
      // The whole screen (12 Oct, Rusty): the map and the screenshots need it.
      width="screen"
      dense
    >
      <div className="flex flex-col gap-6">
        <ProcessMap content={CONTENT} roles={shown} onLane={goToLane} />

        {language.startsWith("en") ? null : (
          <Alert tone="info">
            <span className="inline-flex items-center gap-2">
              <Icon icon={Info} size="sm" />
              {t("accessTraining.englishOnly")}
            </span>
          </Alert>
        )}

        <SectionCard
          title={t("accessTraining.roles.title")}
          description={
            undetermined.length > 0
              ? t("accessTraining.roles.undetermined")
              : t("accessTraining.roles.intro")
          }
        >
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {TRAINING_ROLES.map((role) => {
              const status = statusOf(role);
              const why = status?.sources?.join("; ") ?? "";
              return (
                <li key={role} className="flex flex-col gap-0.5">
                  <Checkbox.Root
                    checked={shown.includes(role)}
                    onCheckedChange={(checked) => setRole(role, checked === true)}
                    label={t(`accessTraining.role.${role}`)}
                  />
                  {status?.detected && why ? (
                    <Typography as="span" variant="caption" tone="muted" className="ps-7" lang="en">
                      {why}
                    </Typography>
                  ) : undetermined.includes(role) ? (
                    <Typography as="span" variant="caption" tone="muted" className="ps-7">
                      {t("accessTraining.roles.unknown")}
                    </Typography>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </SectionCard>

        {shown.map((role) => {
          const chapter = CONTENT.chapters[role];
          const status = statusOf(role);
          return chapter ? (
            <ChapterView
              key={role}
              role={role}
              title={t(`accessTraining.role.${role}`)}
              chapter={chapter}
              version={data.version}
              status={{
                completed: status?.completed === true,
                outOfDate: status?.out_of_date === true,
              }}
            />
          ) : null;
        })}

        {data.version !== CONTENT.version ? (
          <Alert tone="warning">{t("accessTraining.versionMismatch")}</Alert>
        ) : null}

        <ConfirmRoles />
      </div>
    </PageContainer>
  );
}
